import { FC, useRef, useState } from 'react'
import { BadgeName, Emoji, NormalizedParticipant } from 'src/app/types'
import triangle from '../../assets/triangle.svg'
import useOverflowDetector from 'src/hooks/useOverflowDetector'
import Tooltip from 'rc-tooltip'
import 'rc-tooltip/assets/bootstrap.css'
import BadgeSVG from '../BadgeSVG/BadgeSVG'
import { getBadgeNameFromEmoji } from 'src/utils/helpers'

type TableProps = {
  headers: BadgeName[] | string[]
  data: NormalizedParticipant[]
  onSortAll: () => void
}

const Table: FC<TableProps> = ({ headers, data, onSortAll }) => {
  const [clickAll, setClickAll] = useState(false)
  const tableRef = useRef<HTMLTableElement>(null)
  const { hasHorizontalOverflow } = useOverflowDetector(tableRef)

  const cellStyles = `h-10 py-[10px] px-[22px] border-b border-secondaryGreyHover text-center font-normal first:text-left first:min-w-[156px] first:sticky first:left-0 ${
    headers.length > 2 && 'last:w-full last:text-left last:pl-7'
  }`

  const rowStyles = `[&>*:nth-child(2)]:sticky [&>*:nth-child(2)]:left-[156px] ${
    hasHorizontalOverflow &&
    '[&>*:nth-child(2)]:border-r [&>*:nth-child(2)]:border-r-secondaryGreyHover'
  }`

  const onClick = () => {
    onSortAll()
    setClickAll(!clickAll)
  }

  return (
    <div
      className="max-h-[calc(100vh-155px)] overflow-y-auto table-scrollbar"
      ref={tableRef}
    >
      <table className="border-separate border-spacing-0">
        <thead>
          <tr
            className={`${rowStyles} bg-lightGrey [&>*:nth-child(1)]:z-20 [&>*:nth-child(2)]:z-20 [&>*:nth-child(2)]:pr-[30px]`}
          >
            {headers.map((header) => (
              <th
                className={`${cellStyles} bg-lightGrey last:pl-[22px] sticky top-0 z-10 first:rounded-tl-[10px] last:rounded-tr-[10px]`}
                key={header}
              >
                {header === 'All' ? (
                  <span
                    className="flex items-center justify-end cursor-pointer"
                    onClick={onClick}
                    role="button"
                  >
                    {header}
                    <div
                      className={`ml-1 h-4 w-4 -mr-5 transform transition duration-300 ease-in-out ${
                        clickAll && 'rotate-180'
                      }`}
                    >
                      <img src={triangle} alt="triangle" />
                    </div>
                  </span>
                ) : (
                  <span className="bg-lightGrey">
                    {header === 'Participant' ? (
                      header
                    ) : (
                      <BadgeSVG
                        name={getBadgeNameFromEmoji(header as Emoji)}
                        styles="max-w-none"
                      />
                    )}
                  </span>
                )}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {data.map((element, index) => (
            <tr
              // adding index as key here to make the key unique because participant name might be the same sometimes
              key={`${element.Participant}-${index}`}
              className={`${rowStyles} [&>*:nth-child(1)]:z-10 [&>*:nth-child(2)]:z-10`}
            >
              {headers.map((header) => (
                <td
                  className={`${cellStyles} bg-white ${
                    index === data.length - 1 && 'sticky bottom-0 border-t'
                  } ${index === data.length - 2 && 'border-b-0'}`}
                  key={header}
                >
                  {header === 'Participant' && element[header] !== 'Total' ? (
                    <Tooltip
                      placement="bottomLeft"
                      showArrow={false}
                      overlay={element[header]}
                      trigger={['hover']}
                      overlayStyle={{ opacity: 1 }}
                      overlayInnerStyle={{
                        backgroundColor: 'white',
                        color: 'black',
                        borderRadius: 0,
                        minHeight: 0,
                        padding: '0 2px',
                        marginTop: '-2px',
                        fontSize: '12px',
                      }}
                    >
                      <span className="line-clamp">{element[header]}</span>
                    </Tooltip>
                  ) : (
                    <span>{element[header as Emoji]}</span>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default Table
